import React, { useContext, useEffect, useRef, useState } from "react";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import {
  createCampaign,
  updateCampaign,
  uploadCampaignMedia,
  cancelCampaign as cancelCampaignApi,
  restartCampaign as restartCampaignApi,
} from "../../services/campaigns.api";
import { getCampaignTimezoneSync } from "../../helpers/getCampaignTimezone";
import { calculateEstimatedTime } from "../../utils/campaignCalculations";
import { validateCampaignForm } from "./utils/validateCampaignForm";
import useCampaignAttachment from "./hooks/useCampaignAttachment";
import useCampaignData from "./hooks/useCampaignData";

// D8.16 — Lógica (16 estados + effects + handlers) de CampaignModal.
export default function useCampaignModal({ open, onClose, campaignId, initialValues, onSave, resetPagination, campaignType = "UNICA" }) {
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);
  const { companyId } = user;

  const [formKey, setFormKey] = useState(0); // ✅ Key para forzar reinicialización de Formik
  /** Ref a setFieldValue del Formik activo: sin enableReinitialize, last-active debe empujar scheduledAt al formulario */
  const scheduleFormSyncRef = useRef(null);
  const lastWrittenNameRef = useRef(""); // ✅ CRÍTICO: Rastrear el último nombre que el usuario escribió
  const [messageTab, setMessageTab] = useState(0);
  const [configTab, setConfigTab] = useState(0); // Tab de configuración (MENSAJE, INTERVALO, ETIQUETADO, RECURRENCIA)
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [campaignEditable, setCampaignEditable] = useState(true);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [datePastModalOpen, setDatePastModalOpen] = useState(false);
  const [datePastMessage, setDatePastMessage] = useState("");
  const [pendingSetFieldValue, setPendingSetFieldValue] = useState(null);
  const [pendingSaveValues, setPendingSaveValues] = useState(null);
  const saveCampaignRef = useRef(null);
  const [shortTimeModalOpen, setShortTimeModalOpen] = useState(false);
  const [shortTimeMessage, setShortTimeMessage] = useState("");
  const [pendingScheduledAt, setPendingScheduledAt] = useState(null);

  /** §0290: undefined = nueva campaña sin decisión wizard; 'no'|'yes' tras elegir o al editar */
  const [campaignAiChoice, setCampaignAiChoice] = useState(undefined);
  const [wizardLocalChoice, setWizardLocalChoice] = useState("");

  const onResetAttachmentRef = useRef(() => {});

  const {
    campaign,
    setCampaign,
    resetCampaign,
    contactLists,
    whatsapps,
    tagLists,
    companyTimezone,
    promptsList,
    setPromptsList,
    file,
    selectedContactListCount,
    setSelectedContactListCount,
  } = useCampaignData({
    companyId,
    campaignId,
    open,
    campaignType,
    initialValues,
    campaignAiChoice,
    setCampaignAiChoice,
    onFormKeyIncrement: () => setFormKey((k) => k + 1),
    onResetAttachment: () => onResetAttachmentRef.current(),
    onSetMessageTab: setMessageTab,
    onSetConfigTab: setConfigTab,
    onSetWizardLocalChoice: setWizardLocalChoice,
  });

  const {
    attachment,
    setAttachment,
    attachmentFile,
    isFileListSelected,
    setIsFileListSelected,
    isAttachmentSelected,
    setIsAttachmentSelected,
    formatInvalidModalOpen,
    setFormatInvalidModalOpen,
    formatInvalidMessage,
    sizeExceededModalOpen,
    setSizeExceededModalOpen,
    sizeExceededMessage,
    attachmentInfo,
    setAttachmentInfo,
    handleAttachFile,
    handleAttachmentFile,
    deleteMedia,
  } = useCampaignAttachment({ campaign, setCampaign });

  onResetAttachmentRef.current = () => {
    setAttachment(null);
    setAttachmentInfo(null);
    setIsFileListSelected(false);
    setIsAttachmentSelected(false);
    if (attachmentFile.current) attachmentFile.current.value = '';
  };

  // ✅ FASE 4: Determinar tipo de campaña (UNICA o RECURRENTE)
  const currentCampaignType = React.useMemo(() => {
    if (campaignId) {
      // ✅ CORRECCIÓN CRÍTICA: Usar el campo 'tipo' como fuente de verdad principal
      // Si 'tipo' está establecido y es válido, usarlo directamente
      if (campaign.tipo === 'UNICA' || campaign.tipo === 'RECURRENTE') {
        return campaign.tipo;
      }
      
      // ✅ Si 'tipo' no está establecido, determinar desde otros campos
      // 1. Si isRecurring es explícitamente false, es UNICA
      if (campaign.isRecurring === false || campaign.isRecurring === 0 || campaign.isRecurring === '0') {
        return 'UNICA';
      }
      // 2. Si isRecurring es explícitamente true, es RECURRENTE
      if (campaign.isRecurring === true || campaign.isRecurring === 1 || campaign.isRecurring === '1') {
        return 'RECURRENTE';
      }
      // 3. Si tiene campos de recurrencia configurados, es RECURRENTE
      if (campaign.recurrenceType || campaign.recurrenceInterval) {
        return 'RECURRENTE';
      }
      // 4. Por defecto, es UNICA
      return 'UNICA';
    }
    // Si no hay campaignId, usar el campaignType del prop
    return campaignType || 'UNICA';
  }, [campaignId, campaign.tipo, campaign.isRecurring, campaign.recurrenceType, campaign.recurrenceInterval, campaignType]);

  // Función para manejar cambios en lista de contactos
  const handleContactListChange = (value, setFieldValue) => {
    setFieldValue("contactListId", value);
    // ✅ CORRECCIÓN: Limpiar error de nombre duplicado cuando el usuario cambia la lista de contactos
    if (errorMessage && (errorMessage.includes("nombre de campaña") || errorMessage.includes("ya existe"))) {
      setErrorMessage("");
      setErrorModalOpen(false);
    }
    if (value && value !== "") {
      setFieldValue("tagListId", "");
      // Buscar la lista seleccionada para obtener el conteo
      const selectedList = contactLists.find(list => list.id === parseInt(value));
      if (selectedList && selectedList.contactsCount !== undefined) {
        setSelectedContactListCount(selectedList.contactsCount);
      } else {
        setSelectedContactListCount(null);
      }
    } else {
      setSelectedContactListCount(null);
    }
  };

  // Función para manejar cambios en lista de etiquetas
  const handleTagListChange = (value, setFieldValue) => {
    setFieldValue("tagListId", value);
    // ✅ CORRECCIÓN: Limpiar error de nombre duplicado cuando el usuario cambia la lista de etiquetas
    if (errorMessage && (errorMessage.includes("nombre de campaña") || errorMessage.includes("ya existe"))) {
      setErrorMessage("");
      setErrorModalOpen(false);
    }
    if (value && value !== "") {
      setFieldValue("contactListId", "");
    }
  };

  // Función para manejar cambios en lista de archivos
  const handleFileListChange = (value, setFieldValue) => {
    setFieldValue("fileListId", value);
    setIsFileListSelected(value && value !== "");
    if (value && value !== "") {
      setIsAttachmentSelected(false);
    }
  };

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Nueva campaña: sugerencia de fecha (last-active) solo actualiza `campaign`; empujar a Formik
  useEffect(() => {
    if (!open || campaignId) return;
    const sa = campaign.scheduledAt;
    if (!sa) return;
    const setF = scheduleFormSyncRef.current;
    if (typeof setF !== "function") return;
    setF("scheduledAt", sa);
  }, [open, campaignId, campaign.scheduledAt]);

  // ✅ Pestaña RESPONDER (solo IA): índice al final — UNICA 3 | RECURRENTE 4
  const responderConfigTab =
    currentCampaignType === 'RECURRENTE' ? 4 : 3;

  // ✅ Clamp configTab cuando desaparece RESPONDER o al editar tipo
  useEffect(() => {
    if (!open) return;
    if (campaignAiChoice === undefined) return;
    const maxAllowed =
      campaignAiChoice === "yes"
        ? responderConfigTab
        : currentCampaignType === "RECURRENTE"
          ? 3
          : 2;
    if (configTab > maxAllowed) {
      setConfigTab(0);
    }


    const now = moment();
    
    // ✅ REGLA 1: INATIVA siempre editable
    if (campaign.status === "INATIVA") {
      setCampaignEditable(true);
      return;
    }
    
    // ✅ REGLA 2: EM_ANDAMENTO nunca editable
    if (campaign.status === "EM_ANDAMENTO") {
      setCampaignEditable(false);
      return;
    }
    
    // ✅ REGLA 2.1: FINALIZADA - ÚNICAS en solo lectura, RECURRENTES no editables
    if (campaign.status === "FINALIZADA") {
      setCampaignEditable(false);
      return;
    }
    
    // ✅ REGLA 3: CANCELADA siempre editable (pausada o parada)
    if (campaign.status === "CANCELADA") {
      setCampaignEditable(true);
      return;
    }
    
    // ✅ REGLA 4: PROGRAMADA - Validar tiempo mínimo (10 minutos)
    if (campaign.status === "PROGRAMADA") {
      // Para campañas recurrentes activas, validar nextScheduledAt
      const timeToCheck = campaign.isRecurring && campaign.executionCount > 0 && campaign.nextScheduledAt
        ? moment(campaign.nextScheduledAt)
        : moment(campaign.scheduledAt);
      
      if (!timeToCheck.isValid()) {
        setCampaignEditable(false);
        return;
      }
      
      const minutesUntilExecution = timeToCheck.diff(now, "minutes");
      const hasEnoughTime = minutesUntilExecution > 10;
      
      setCampaignEditable(hasEnoughTime);
      return;
    }
    
    // ✅ Por defecto, no editable
    setCampaignEditable(false);
  }, [campaign.status, campaign.scheduledAt, campaign.nextScheduledAt, campaign.isRecurring, campaign.executionCount]);

  // ✅ NOTA: El cálculo de tiempo estimado ahora se hace directamente en el render
  // usando `currentCalculatedTime` calculado con los valores de Formik `values`

  const handleClose = () => {
    onClose();
    // ✅ CORRECCIÓN: Resetear TODO el estado del formulario
    resetCampaign();
    setFormKey(prev => prev + 1); // ✅ Forzar reinicialización de Formik cambiando la key
    lastWrittenNameRef.current = ""; // ✅ CRÍTICO: Limpiar el ref del último nombre escrito
    setAttachment(null);
    setAttachmentInfo(null);
    setIsFileListSelected(false);
    setIsAttachmentSelected(false);
    setSelectedContactListCount(null);
    setMessageTab(0);
    setConfigTab(0); // ✅ FASE 4: Resetear tab de configuración
    setCampaignAiChoice(undefined);
    setWizardLocalChoice("");
    setPromptsList([]);
    setErrorMessage(""); // ✅ Limpiar mensajes de error
    setErrorModalOpen(false); // ✅ Cerrar modales de error
    // Limpiar input de archivo si existe
    if (attachmentFile.current) {
      attachmentFile.current.value = '';
    }
  };

  const handleSaveCampaign = async (values, shouldSkipDateCheck = false, shouldSkipShortTimeCheck = false, setFieldValue = null) => {
    try {
      // ✅ Validar fecha programada en el pasado o < 10 minutos (filtro adicional en botón AGREGAR)
      if (!shouldSkipDateCheck && values.scheduledAt && values.scheduledAt !== "" && values.scheduledAt !== null) {
        // ✅ CORRECCIÓN: Usar zona horaria sincronizada desde Settings
        const timezone = getCampaignTimezoneSync();
        
        // Parsear scheduledAt en zona empresa (viene como string "YYYY-MM-DD HH:mm:ss")
        const scheduledMoment = moment.tz(values.scheduledAt, 'YYYY-MM-DD HH:mm:ss', timezone);
        
        // ✅ DEBUG: Verificar parseo
        if (!scheduledMoment.isValid()) {
          console.warn(`[CampaignModal] ⚠️ Fecha inválida al parsear:`, values.scheduledAt, timezone);
          // Si la fecha es inválida, continuar sin validación
        } else {
          const nowInCompanyTimezone = moment().tz(timezone);
          const diffMinutes = scheduledMoment.diff(nowInCompanyTimezone, 'minutes', true);
          
          // ✅ DEBUG: Log para validación de 10 minutos (usar console.log para que siempre se vea)
          // ✅ COMENTADO: Spam de logs deshabilitado
          // console.log(`[CampaignModal] 🔍 Validación de fecha:`, {
          //   scheduledAt: values.scheduledAt,
          //   scheduledMoment: scheduledMoment.format('YYYY-MM-DD HH:mm:ss'),
          //   nowInCompanyTimezone: nowInCompanyTimezone.format('YYYY-MM-DD HH:mm:ss'),
          //   diffMinutes: diffMinutes.toFixed(2),
          //   isBefore: scheduledMoment.isBefore(nowInCompanyTimezone),
          //   shouldSkipShortTimeCheck,
          //   shouldSkipDateCheck
          // });
          
          if (scheduledMoment.isBefore(nowInCompanyTimezone)) {
            // Fecha pasada
            // console.log(`[CampaignModal] ⚠️ Fecha en el pasado detectada`);
            const pastDiffMinutes = nowInCompanyTimezone.diff(scheduledMoment, 'minutes');
            const formattedDate = scheduledMoment.format('DD/MM/YYYY HH:mm');
            setDatePastMessage(
              `La fecha programada (${formattedDate}) está en el pasado (hace ${pastDiffMinutes} minutos).\n\n¿Desea actualizar la fecha a ahora mismo (hora actual + 2 minutos) para enviar la campaña inmediatamente?`
            );
            setPendingSaveValues(values);
            // Guardar referencia a setFieldValue para poder actualizar el campo
            setPendingSetFieldValue(setFieldValue);
            saveCampaignRef.current = (vals, skipDate, skipShort) => handleSaveCampaign(vals, skipDate, skipShort, null);
            setDatePastModalOpen(true);
            // Esperar respuesta del usuario antes de continuar
            return;
          } else if (diffMinutes > 0 && diffMinutes < 10 && !shouldSkipShortTimeCheck) {
            // ✅ REGLA DE NEGOCIO: Fecha futura pero < 10 minutos - mostrar modal informativo
            // console.log(`[CampaignModal] ⚠️ ALERTA: Fecha a menos de 10 minutos (${diffMinutes.toFixed(2)} minutos)`);
            const formattedDate = scheduledMoment.format('DD/MM/YYYY HH:mm');
            setShortTimeMessage(
              `La fecha programada (${formattedDate}) está a menos de 10 minutos (${Math.ceil(diffMinutes)} minutos). Si programa con menos de 10 minutos, no tendrá opción a modificar esta campaña ya que los tiempos son muy cortos para su procesamiento. Se sugiere programar envíos mayores a 30 minutos.`
            );
            setPendingSaveValues(values);
            setPendingScheduledAt(values.scheduledAt); // ✅ Guardar el valor de scheduledAt
            saveCampaignRef.current = (vals, skipDate, skipShort) => handleSaveCampaign(vals, skipDate, skipShort, null);
            setShortTimeModalOpen(true);
            // Esperar respuesta del usuario antes de continuar
            return;
          } else {
            // console.log(`[CampaignModal] ✅ Validación de fecha pasada: diffMinutes=${diffMinutes.toFixed(2)}, dentro de rango permitido`);
          }
        }
      }

      // ✅ CRÍTICO: Usar el último nombre que el usuario escribió (fuente de verdad)
      // El ref mantiene el valor más reciente que el usuario escribió, independientemente de reinicializaciones de Formik
      const finalName = lastWrittenNameRef.current && lastWrittenNameRef.current.trim() !== "" 
        ? lastWrittenNameRef.current 
        : (campaign.name && campaign.name.trim() !== "" ? campaign.name : (values.name || ""));

      // ✅ Obtener zona horaria de la empresa para campañas (desde estado sincronizado)
      const timezone = getCampaignTimezoneSync();
      
      const dataValues = {};
      Object.entries(values).forEach(([key, value]) => {
        // ✅ CRÍTICO: Para el campo 'name', usar el valor más reciente (finalName tiene prioridad)
        if (key === "name") {
          dataValues[key] = finalName || value || "";
          return;
        }
        if (key === "scheduledAt" && value !== "" && value !== null) {
          // ✅ CORRECCIÓN: DateTimePickerWithTimezone ya devuelve string en formato "YYYY-MM-DD HH:mm:ss"
          // en la zona horaria de la empresa, así que solo validamos y usamos directamente
          // El componente ya hizo la conversión correcta
          
          if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
            // ✅ Ya viene en formato correcto, usar directamente
            dataValues[key] = value;
          } else {
            // ⚠️ PROBLEMA: Este fallback aplica conversión de timezone incorrecta
            // Si el componente devuelve el formato correcto, este código no debería ejecutarse
            const browserMoment = moment(value);
            const companyMoment = browserMoment.clone().tz(timezone);
            dataValues[key] = companyMoment.format("YYYY-MM-DD HH:mm:ss");
          }
        } else if (key === "recurrenceEndDate" && value !== "" && value !== null) {
          // ✅ Misma lógica para recurrenceEndDate
          const browserMoment = moment(value);
          const companyMoment = browserMoment.clone().tz(timezone);
          dataValues[key] = companyMoment.format("YYYY-MM-DD HH:mm:ss");
        } else if (key === "recurrenceInterval") {
          // ✅ Si es recurrente y el intervalo está vacío/null, usar valor por defecto 1
          if (values.isRecurring && (value === "" || value === null || value === undefined)) {
            dataValues[key] = 1;
          } else {
            dataValues[key] = value === "" ? null : value;
          }
        } else {
          dataValues[key] = value === "" ? null : value;
        }
      });

      const aiOnSave = campaignAiChoice === "yes";
      if (!aiOnSave) {
        dataValues.promptId = null;
        dataValues.replyPromptId = null;
      } else {
        const rawPid = values.promptId;
        const pn = typeof rawPid === "string" ? parseInt(String(rawPid).trim(), 10) : Number(rawPid);
        dataValues.promptId = Number.isFinite(pn) && !Number.isNaN(pn) ? pn : null;
        const rawRid = values.replyPromptId;
        const rn = typeof rawRid === "string" ? parseInt(String(rawRid).trim(), 10) : Number(rawRid);
        dataValues.replyPromptId = Number.isFinite(rn) && !Number.isNaN(rn) ? rn : null;
        [2, 3, 4, 5].forEach((m) => {
          dataValues[`message${m}`] = null;
          dataValues[`confirmationMessage${m}`] = null;
        });
        dataValues.confirmation = false;
        dataValues.autoTagEnabled = true;
      }

      ["prompt", "replyPrompt", "shipping", "contactList", "whatsapp"].forEach((k) => delete dataValues[k]);
      /** Update: tenant por registro — evita companyId viejo/desincronizado en Formik (ERR_NO_CAMPAIGN_FOUND al ORM-update). Create sigue enviándolo desde el usuario. */
      if (campaignId) {
        delete dataValues.companyId;
      }

      if (campaignId) {
        await updateCampaign(campaignId, dataValues);

        if (attachment != null) {
          try {
          const formData = new FormData();
          formData.append("file", attachment);
          await uploadCampaignMedia(campaignId, formData);
          } catch (uploadError) {
            console.error("Error al subir archivo:", uploadError);
            const uploadErrorMsg = uploadError?.response?.data?.error || uploadError?.message || "Error al subir archivo";
            toast.error(`Campaña actualizada, pero hubo un error al subir el archivo: ${uploadErrorMsg}`);
        }
        }
        
        // ✅ CORRECCIÓN: El backend ya emite el mensaje socket, no es necesario hacerlo desde el frontend
        // El backend emite en CampaignController.update() después de UpdateService
        handleClose();
      } else {
        const { data } = await createCampaign(dataValues);

        if (attachment != null) {
          try {
          const formData = new FormData();
          formData.append("file", attachment);
          await uploadCampaignMedia(data.id, formData);
          } catch (uploadError) {
            console.error("Error al subir archivo:", uploadError);
            const uploadErrorMsg = uploadError?.response?.data?.error || uploadError?.message || "Error al subir archivo";
            toast.warning(`Campaña creada exitosamente, pero hubo un error al subir el archivo: ${uploadErrorMsg}`);
          }
        }
        if (onSave) {
          onSave(data);
        }
        // ✅ CORRECCIÓN: Resetear formulario COMPLETAMENTE antes de cerrar el modal
        // Esto asegura que el formulario esté limpio para la próxima creación
        // ✅ CRÍTICO: Verificar que el componente esté montado antes de actualizar estados
        if (isMounted.current) {
          resetCampaign();
          setFormKey(prev => prev + 1); // ✅ CRÍTICO: Forzar reinicialización completa de Formik
          lastWrittenNameRef.current = ""; // ✅ CRÍTICO: Limpiar el ref del último nombre escrito
          setAttachment(null);
          setAttachmentInfo(null);
          setIsFileListSelected(false);
          setIsAttachmentSelected(false);
          setSelectedContactListCount(null);
          setMessageTab(0);
          setConfigTab(0);
          setErrorMessage(""); // ✅ Limpiar mensajes de error
          setErrorModalOpen(false); // ✅ Cerrar modales de error
          if (attachmentFile.current) {
            attachmentFile.current.value = '';
        }
        handleClose();
        }
      }
      toast.success(i18n.t("campaigns.toasts.success"));
    } catch (err) {
      console.log(err);
      const errorMsg = err?.response?.data?.error || err?.message || String(err);
      const errorCode = err?.response?.data?.code;
      
      // ✅ CRÍTICO: Verificar que el componente esté montado antes de actualizar estados
      if (!isMounted.current) {
        return; // Componente desmontado, no actualizar estados
      }
      
      // ✅ CORRECCIÓN: Verificar si el error es de tiempo < 10 minutos (solo en edición)
      if (campaignId && (errorCode === "ERR_CAMPAIGN_SHORT_TIME" || (errorMsg.includes("10 minutos") && errorMsg.includes("anticipación")))) {
        setShortTimeMessage(errorMsg);
        setPendingSaveValues(values); // Guardar valores para referencia
        saveCampaignRef.current = (vals, skipDate, skipShort) => handleSaveCampaign(vals, skipDate, skipShort, null);
        setShortTimeModalOpen(true);
        return; // No mostrar toast, el modal se encargará
      }
      
      // Verificar si el error es de nombre duplicado
      if (errorMsg.includes("nombre de campaña") && errorMsg.includes("ya existe")) {
        // ✅ CORRECCIÓN: NO limpiar el campo name - el usuario debe poder corregirlo sin perder el valor
        // El error se limpiará automáticamente cuando el usuario cambie el nombre (ver onChange del campo name)
        setErrorMessage(errorMsg);
        setErrorModalOpen(true);
        // ✅ IMPORTANTE: NO cerrar el modal ni resetear el formulario - el usuario debe poder corregir el nombre
      }
      // ✅ MEJORA UX: Verificar si el error es de conflicto de horarios
      else if (errorMsg.includes("Horario no disponible") || errorMsg.includes("Conflicto con") || errorMsg.includes("Siguiente disponible")) {
        setErrorMessage(errorMsg);
        setErrorModalOpen(true);
      }
      // ✅ NUEVO: Verificar si el error es de solapamiento de campañas
      else if (errorMsg.includes("No se puede programar la campaña")) {
        setErrorMessage(errorMsg);
        setErrorModalOpen(true);
      }
      // ✅ MEJORA UX: Cambiar errores de validación importantes a MODAL en lugar de toast
      else if (errorMsg.includes("conexión WhatsApp") || errorMsg.includes("WhatsApp") || errorMsg.includes("no está activa")) {
        setErrorMessage(errorMsg);
        setErrorModalOpen(true);
      }
      else if (errorMsg.includes("lista de contactos") || errorMsg.includes("contactListId") || errorMsg.includes("Lista de contactos")) {
        setErrorMessage(errorMsg);
        setErrorModalOpen(true);
      }
      else if (errorMsg.includes("No se proporcionó ningún archivo") || errorMsg.includes("archivo")) {
        setErrorMessage(errorMsg);
        setErrorModalOpen(true);
      }
      else {
        // Solo errores genéricos/no críticos usan toast
        toastError(err);
      }
    }
  };

  const cancelCampaign = async () => {
    try {
      await cancelCampaignApi(campaign.id);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setCampaign((prev) => ({ ...prev, status: "CANCELADA" }));
      resetPagination();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async () => {
    try {
      await restartCampaignApi(campaign.id);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setCampaign((prev) => ({ ...prev, status: "EM_ANDAMENTO" }));
      resetPagination();
    } catch (err) {
      // ✅ MEJORA UX: Mostrar errores importantes en modal
      const errorMsg = err?.response?.data?.error || err?.message || String(err);
      if (errorMsg.includes("No es permitido") || errorMsg.includes("en proceso") || errorMsg.includes("FINALIZADA")) {
        setErrorMessage(errorMsg);
        setErrorModalOpen(true);
      } else {
        toast.error(err.message);
      }
    }
  };

  return {
    attachment,
    attachmentFile,
    attachmentInfo,
    campaign,
    campaignAiChoice,
    campaignEditable,
    cancelCampaign,
    companyId,
    companyTimezone,
    configTab,
    confirmationOpen,
    contactLists,
    currentCampaignType,
    datePastMessage,
    datePastModalOpen,
    deleteMedia,
    errorMessage,
    errorModalOpen,
    file,
    formKey,
    formatInvalidMessage,
    formatInvalidModalOpen,
    handleAttachFile,
    handleAttachmentFile,
    handleClose,
    handleContactListChange,
    handleFileListChange,
    handleSaveCampaign,
    handleTagListChange,
    isAttachmentSelected,
    isFileListSelected,
    isMounted,
    lastWrittenNameRef,
    messageTab,
    onResetAttachmentRef,
    pendingSaveValues,
    pendingScheduledAt,
    pendingSetFieldValue,
    promptsList,
    resetCampaign,
    responderConfigTab,
    restartCampaign,
    saveCampaignRef,
    scheduleFormSyncRef,
    selectedContactListCount,
    setAttachment,
    setAttachmentInfo,
    setCampaign,
    setCampaignAiChoice,
    setCampaignEditable,
    setConfigTab,
    setConfirmationOpen,
    setDatePastMessage,
    setDatePastModalOpen,
    setErrorMessage,
    setErrorModalOpen,
    setFormKey,
    setFormatInvalidModalOpen,
    setIsAttachmentSelected,
    setIsFileListSelected,
    setMessageTab,
    setPendingSaveValues,
    setPendingScheduledAt,
    setPendingSetFieldValue,
    setPromptsList,
    setSelectedContactListCount,
    setShortTimeMessage,
    setShortTimeModalOpen,
    setSizeExceededModalOpen,
    setWizardLocalChoice,
    shortTimeMessage,
    shortTimeModalOpen,
    sizeExceededMessage,
    sizeExceededModalOpen,
    tagLists,
    user,
    whatsapps,
    wizardLocalChoice,
  };
}
